import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Globe } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { scrollToId, getWhatsAppLink } from "@/lib/site";
import { useLanguage, LANGS } from "@/lib/i18n";

const LINKS = [
  { id: "cardapio", key: "cardapio", testid: "nav-link-cardapio" },
  { id: "historia", key: "historia", testid: "nav-link-historia" },
  { id: "como-chegar", key: "comoChegar", testid: "nav-link-como-chegar" },
];

function LangSwitcher({ compact = false }) {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid="language-switcher"
      className={`flex items-center gap-0.5 rounded-full border border-[#3D2B1F]/12 bg-[#FCFBF7]/70 p-1 ${compact ? "" : ""}`}
      role="group"
      aria-label="Language / Idioma / Idioma"
    >
      <Globe size={13} className="mx-1.5 text-[#0D5C63]" />
      {LANGS.map((l) => (
        <button
          key={l.id}
          data-testid={`lang-${l.id}`}
          onClick={() => setLang(l.id)}
          aria-pressed={lang === l.id}
          className={`rounded-full px-2.5 py-1 font-jetmono text-[10px] tracking-[0.12em] transition-all duration-300 ${
            lang === l.id
              ? "bg-[#0D5C63] text-[#FAF6EE]"
              : "text-[#1A252C]/55 hover:text-[#E76F51]"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function HeaderNav() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 120 : 0);
  };

  return (
    <header
      data-testid="navbar"
      className="fixed top-3 left-1/2 z-50 w-[min(1120px,calc(100%-1.25rem))] -translate-x-1/2"
    >
      <div className="flex items-center justify-between gap-3 rounded-full border border-[#3D2B1F]/10 bg-[#FAF6EE]/85 px-4 py-2.5 shadow-lg shadow-[#3D2B1F]/5 backdrop-blur-xl sm:px-6">
        <button
          data-testid="nav-brand"
          onClick={() => go("hero")}
          className="group flex items-center gap-2.5"
        >
          <span className="text-left leading-none">
            <span className="block font-cabinet text-base font-extrabold tracking-tight text-[#1A252C] sm:text-lg">
              Quiosque Rainha do Mar
            </span>
            <span className="mt-1 block font-jetmono text-[10px] uppercase tracking-[0.25em] text-[#0D5C63]">
              Sete Fontes · Ubatuba
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navegação principal">
          {LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={link.testid}
              onClick={() => go(link.id)}
              className="font-jetmono text-[11px] uppercase tracking-[0.22em] text-[#1A252C]/70 transition-colors duration-300 hover:text-[#E76F51]"
            >
              {t.nav[link.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:block">
            <LangSwitcher />
          </div>
          <a
            data-testid="nav-whatsapp-button"
            href={getWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden h-10 w-10 place-items-center rounded-full border border-[#3D2B1F]/15 text-[#0D5C63] transition-colors duration-300 hover:bg-[#0D5C63] hover:text-[#FAF6EE] sm:grid"
            aria-label={t.nav.whatsappAria}
          >
            <WhatsAppIcon size={17} />
          </a>
          <button
            data-testid="nav-reserve-button"
            onClick={() => go("reservar")}
            className="hidden items-center rounded-full bg-[#E76F51] px-5 py-2.5 font-cabinet text-sm font-bold text-[#FAF6EE] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#D85C3D] sm:inline-flex"
          >
            {t.nav.reservar}
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-full border border-[#3D2B1F]/15 text-[#1A252C] lg:hidden"
            aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
          >
            {open ? <X size={18} /> : <MenuIcon size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="mt-2 rounded-3xl border border-[#3D2B1F]/10 bg-[#FAF6EE]/95 p-4 shadow-xl shadow-[#3D2B1F]/10 backdrop-blur-xl lg:hidden"
          >
            {LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-mobile-${link.id}`}
                onClick={() => go(link.id)}
                className="block w-full rounded-2xl px-4 py-3.5 text-left font-cabinet text-lg font-bold text-[#1A252C] transition-colors hover:bg-[#F4EBD9]"
              >
                {t.nav[link.key]}
              </button>
            ))}
            <button
              data-testid="nav-mobile-reservar"
              onClick={() => go("reservar")}
              className="mt-2 block w-full rounded-2xl bg-[#E76F51] px-4 py-3.5 font-cabinet text-lg font-bold text-[#FAF6EE]"
            >
              {t.nav.reservarMobile}
            </button>
            <div className="mt-3 flex justify-center md:hidden">
              <LangSwitcher />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
